import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import {
  Plus, Pencil, Trash2, X, Check, GripVertical,
  Eye, EyeOff, QrCode, ArrowLeft, Download, Music
} from 'lucide-react';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, useSortable, verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import api from '../../api/client';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { useAuth } from '../../context/AuthContext';

// ── Sortable Chapter Row ──────────────────────────────
function SortableChapterRow({ ch, tutorialKey, onEdit, onDelete, onToggle, onQR }) {
  const { can } = useAuth();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: ch.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };

  const ytThumb = ch.videoUrl && ch.videoUrl.length <= 20
    ? `https://swardhara-data-store.blr1.cdn.digitaloceanspaces.com/data/image/${ch.key_value.slice(0, 5)}/${ch.key_value.slice(0, 5)}.png`
    : null;

  return (
    <tr ref={setNodeRef} style={style}>
      <td style={{ width: 36 }}>
        {can('chapters','edit') && (
          <span {...attributes} {...listeners} style={{ cursor: 'grab', color: 'var(--grey-300)', display: 'flex' }}>
            <GripVertical size={16} />
          </span>
        )}
      </td>
      <td style={{ width: 56 }}>
        {ytThumb
          ? <img src={ytThumb} alt="" style={{ width: 52, height: 36, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--grey-100)' }} />
          : <div style={{ width: 52, height: 36, borderRadius: 6, background: 'var(--orange-50)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Music size={16} style={{ color: 'var(--orange-300)' }} />
            </div>
        }
      </td>
      <td>
        <div style={{ fontWeight: 600, fontSize: 13.5, color: 'var(--grey-800)' }}>{ch.name}</div>
        {ch.videoUrl && (
          <a href={`https://youtube.com/watch?v=${ch.videoUrl}`} target="_blank" rel="noreferrer"
            style={{ fontSize: 11, color: 'var(--orange-500)' }}>
            yt: {ch.videoUrl}
          </a>
        )}
      </td>
      <td>
        {ch.key_value
          ? <code style={{ fontSize: 11, background: 'var(--orange-50)', padding: '2px 7px', borderRadius: 4, color: 'var(--orange-600)' }}>{ch.key_value}</code>
          : <span style={{ color: 'var(--grey-300)' }}>—</span>}
      </td>
      <td>
        <div style={{ display: 'flex', gap: 4 }}>
          {ch.pdf_english && <span className="badge badge-blue" style={{ fontSize: 10 }}>PDF EN</span>}
          {ch.pdf_hindi   && <span className="badge badge-orange" style={{ fontSize: 10 }}>PDF HI</span>}
          {ch.pdf_marathi && <span className="badge badge-green" style={{ fontSize: 10 }}>PDF MR</span>}
          {ch.audio_file  && <span className="badge badge-grey" style={{ fontSize: 10 }}>Audio</span>}
        </div>
      </td>
      <td>
        <label className="toggle">
          <input type="checkbox" checked={!!ch.is_preview}
            onChange={() => can('chapters','edit') && onToggle(ch, 'is_preview', !ch.is_preview)} />
          <span className="toggle-track"><span className="toggle-thumb" /></span>
        </label>
      </td>
      <td>
        <label className="toggle">
          <input type="checkbox" checked={!ch.disable}
            onChange={() => can('chapters','edit') && onToggle(ch, 'disable', !ch.disable)} />
          <span className="toggle-track"><span className="toggle-thumb" /></span>
        </label>
      </td>
      <td>
        <div style={{ display: 'flex', gap: 4 }}>
          {ch.key_value && tutorialKey && (
            <button className="btn-icon" title="Preview QR Codes" onClick={() => onQR(ch)}><QrCode size={14} /></button>
          )}
          {can('chapters','edit') && (
            <button className="btn-icon" onClick={() => onEdit(ch)}><Pencil size={14} /></button>
          )}
          {can('chapters','delete') && (
            <button className="btn-icon" onClick={() => onDelete(ch.id)}><Trash2 size={14} /></button>
          )}
        </div>
      </td>
    </tr>
  );
}

// ── QR Preview Modal ─────────────────────────────────
function QRModal({ chapterId, onClose }) {
  const [qrData, setQrData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get(`/qr/chapter/${chapterId}`);
        setQrData(data.data);
      } catch { toast.error('Failed to load QR codes'); }
      finally { setLoading(false); }
    };
    load();
  }, [chapterId]);

  const downloadQR = (dataUrl, name) => {
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = `${name}.png`;
    a.click();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title"><QrCode size={18} /> QR Codes — {qrData?.chapter_key}</span>
          <button className="btn-icon" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="modal-body">
          {loading ? <div className="loader-wrap"><div className="loader" /></div> : qrData ? (
            <>
              <div className="alert alert-info" style={{ marginBottom: 16, fontSize: 12 }}>
                QR URL pattern: <code style={{ fontFamily: 'monospace' }}>
                  https://prod.swardharaapp.com/qr/{qrData.tutorial_key}/{qrData.chapter_key.replace(qrData.tutorial_key,'')}/LANG
                </code>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 20 }}>
                {Object.entries(qrData.qrCodes).map(([lang, info]) => (
                  <div key={lang} style={{ textAlign: 'center', border: '1px solid var(--grey-200)', borderRadius: 10, padding: 16 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--orange-600)', letterSpacing: '.08em', marginBottom: 10 }}>
                      {lang === 'ENG' ? '🇬🇧 English' : lang === 'HIN' ? '🇮🇳 Hindi' : '🟠 Marathi'}
                    </div>
                    <img src={info.dataUrl} alt={lang} style={{ width: 160, height: 160 }} />
                    <div style={{ marginTop: 10, fontSize: 10, color: 'var(--grey-400)', wordBreak: 'break-all' }}>{info.url}</div>
                    <button className="btn btn-sm btn-secondary" style={{ marginTop: 10, width: '100%' }}
                      onClick={() => downloadQR(info.dataUrl, `${qrData.chapter_key}_${lang}`)}>
                      <Download size={12} /> Download PNG
                    </button>
                  </div>
                ))}
              </div>
            </>
          ) : <p style={{ color: 'var(--grey-400)' }}>No QR data</p>}
        </div>
      </div>
    </div>
  );
}

// ── Chapter Form Modal ────────────────────────────────
function ChapterFormModal({ tutorialId, editing, onClose, onSaved }) {
  const { register, handleSubmit, reset, formState: { errors } } = useForm();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editing) {
      reset({ ...editing, is_preview: !!editing.is_preview, disable: !!editing.disable });
    } else {
      reset({
        name: '', title_hindi: '', title_marathi: '',
        videoUrl: '', pdf_english: '', pdf_hindi: '', pdf_marathi: '',
        audio_file: '', audio_pdf_eng: '', audio_pdf_hin: '', audio_pdf_mar: '',
        key_value: '', is_preview: false, disable: false, tutorialId,
      });
    }
  }, [editing, tutorialId, reset]);

  const onSubmit = async (values) => {
    setSaving(true);
    try {
      if (editing) { await api.put(`/chapters/${editing.id}`, { ...values, tutorialId }); toast.success('Chapter updated'); }
      else         { await api.post('/chapters', { ...values, tutorialId }); toast.success('Chapter created'); }
      onSaved();
    } catch (err) { toast.error(err.response?.data?.message || 'Error'); }
    finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg" onClick={e => e.stopPropagation()} style={{ maxWidth: 820 }}>
        <div className="modal-header">
          <span className="modal-title">{editing ? 'Edit' : 'Add'} Chapter</span>
          <button className="btn-icon" onClick={onClose}><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="modal-body">
            {/* Names */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--orange-600)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 10 }}>Chapter Name</div>
              <div className="form-group">
                <label className="form-label">Name (English) <span className="req">*</span></label>
                <input className={`form-control ${errors.name ? 'error' : ''}`}
                  placeholder="1 - Swar Recognition - Recognize 7 shudh swar"
                  {...register('name', { required: 'Required' })} />
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Name (Hindi)</label>
                  <input className="form-control" {...register('title_hindi')} />
                </div>
                <div className="form-group">
                  <label className="form-label">Name (Marathi)</label>
                  <input className="form-control" {...register('title_marathi')} />
                </div>
              </div>
            </div>

            {/* Video & Key */}
            <div style={{ borderTop: '1px solid var(--grey-100)', paddingTop: 16, marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--orange-600)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 10 }}>Video & Key</div>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">YouTube Video ID</label>
                  <input className="form-control" placeholder="a4A0rdmFNno" {...register('videoUrl')} />
                  <p style={{ fontSize: 11, color: 'var(--grey-400)', marginTop: 3 }}>YouTube video ID only (not full URL)</p>
                </div>
                <div className="form-group">
                  <label className="form-label">Chapter Key</label>
                  <input className="form-control" placeholder="VOL01TRK01" style={{ textTransform: 'uppercase' }} {...register('key_value')} />
                  <p style={{ fontSize: 11, color: 'var(--grey-400)', marginTop: 3 }}>Used for QR code generation</p>
                </div>
              </div>
            </div>

            {/* PDF files */}
            <div style={{ borderTop: '1px solid var(--grey-100)', paddingTop: 16, marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--orange-600)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 10 }}>PDF Files (CDN filenames)</div>
              <div className="form-grid cols-3">
                <div className="form-group">
                  <label className="form-label">PDF English</label>
                  <input className="form-control" placeholder="VOL1TRK1.pdf" {...register('pdf_english')} />
                </div>
                <div className="form-group">
                  <label className="form-label">PDF Hindi</label>
                  <input className="form-control" placeholder="file-xxx.pdf" {...register('pdf_hindi')} />
                </div>
                <div className="form-group">
                  <label className="form-label">PDF Marathi</label>
                  <input className="form-control" placeholder="VOL1TRK1M.pdf" {...register('pdf_marathi')} />
                </div>
              </div>
            </div>

            {/* Audio files */}
            <div style={{ borderTop: '1px solid var(--grey-100)', paddingTop: 16, marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--orange-600)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 10 }}>Audio Files (CDN filenames)</div>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Audio MP3</label>
                  <input className="form-control" placeholder="VOL01TRK01.mp3" {...register('audio_file')} />
                </div>
                <div className="form-group">
                  <label className="form-label">Audio Sheet EN</label>
                  <input className="form-control" placeholder="VOL01TRK01ENG.pdf" {...register('audio_pdf_eng')} />
                </div>
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Audio Sheet HI</label>
                  <input className="form-control" placeholder="VOL01TRK01HIN.pdf" {...register('audio_pdf_hin')} />
                </div>
                <div className="form-group">
                  <label className="form-label">Audio Sheet MR</label>
                  <input className="form-control" placeholder="VOL01TRK01MAR.pdf" {...register('audio_pdf_mar')} />
                </div>
              </div>
            </div>

            {/* Flags */}
            <div style={{ display: 'flex', gap: 28 }}>
              <label className="toggle">
                <input type="checkbox" {...register('is_preview')} />
                <span className="toggle-track"><span className="toggle-thumb" /></span>
                <span style={{ fontSize: 13 }}>Free Preview</span>
              </label>
              <label className="toggle">
                <input type="checkbox" {...register('disable')} />
                <span className="toggle-track"><span className="toggle-thumb" /></span>
                <span style={{ fontSize: 13 }}>Disabled</span>
              </label>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : <><Check size={14} /> Save Chapter</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Main Chapters Page ────────────────────────────────
export default function ChaptersPage() {
  const { tutorialId } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();

  const [tutorial, setTutorial]   = useState(null);
  const [chapters, setChapters]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [modal, setModal]         = useState(false);
  const [editing, setEditing]     = useState(null);
  const [confirm, setConfirm]     = useState(null);
  const [qrChapterId, setQrChapterId] = useState(null);
  const [showDisabled, setShowDisabled] = useState(false);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const load = useCallback(async () => {
    try {
      const [tut, chaps] = await Promise.all([
        api.get(`/tutorials/${tutorialId}`),
        api.get(`/chapters?tutorialId=${tutorialId}&includeDisabled=${showDisabled}`),
      ]);
      setTutorial(tut.data.data);
      setChapters(chaps.data.data);
    } catch { toast.error('Failed to load chapters'); }
    finally { setLoading(false); }
  }, [tutorialId, showDisabled]);

  useEffect(() => { load(); }, [load]);

  const handleDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIndex = chapters.findIndex(c => c.id === active.id);
    const newIndex = chapters.findIndex(c => c.id === over.id);
    const reordered = arrayMove(chapters, oldIndex, newIndex);
    setChapters(reordered);
    try {
      await api.patch('/chapters/reorder', { tutorialId, orderedIds: reordered.map(c => c.id) });
      toast.success('Order saved');
    } catch { toast.error('Failed to save order'); load(); }
  };

  const handleToggle = async (ch, field, value) => {
    try {
      await api.patch(`/chapters/${ch.id}/toggle`, { field, value });
      setChapters(prev => prev.map(c => c.id === ch.id ? { ...c, [field]: value ? 1 : 0 } : c));
    } catch { toast.error('Failed to update'); }
  };

  const doDelete = async (id) => {
    try { await api.delete(`/chapters/${id}`); toast.success('Deleted'); setConfirm(null); load(); }
    catch (err) { toast.error(err.response?.data?.message || 'Error'); setConfirm(null); }
  };

  // Download bulk QR for this tutorial
  const downloadBulkQR = async () => {
    try {
      toast.loading('Generating QR ZIP…', { id: 'qr' });
      const resp = await api.get(`/qr/tutorial/${tutorialId}/bulk`, { responseType: 'blob' });
      const url  = URL.createObjectURL(resp.data);
      const a    = document.createElement('a');
      a.href = url;
      a.download = `${tutorial?.key_value}_QR_Codes.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Downloaded', { id: 'qr' });
    } catch { toast.error('QR generation failed', { id: 'qr' }); }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <button className="btn btn-secondary btn-sm" onClick={() => navigate('/tutorials')} style={{ marginBottom: 8 }}>
            <ArrowLeft size={13} /> Back to Tutorials
          </button>
          <h1 className="page-title">{tutorial?.name || 'Loading…'}</h1>
          <p className="page-subtitle">
            {tutorial?.key_value && <code style={{ fontSize: 12, color: 'var(--orange-500)' }}>{tutorial.key_value}</code>}
            {' '}&mdash; Chapter Management &amp; Drag-to-Reorder
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <label className="toggle">
            <input type="checkbox" checked={showDisabled} onChange={e => setShowDisabled(e.target.checked)} />
            <span className="toggle-track"><span className="toggle-thumb" /></span>
            <span style={{ fontSize: 13 }}>Show disabled</span>
          </label>
          {tutorial?.key_value && (
            <button className="btn btn-secondary" onClick={downloadBulkQR}>
              <Download size={14} /> All QR Codes
            </button>
          )}
          {can('chapters','create') && (
            <button className="btn btn-primary" onClick={() => { setEditing(null); setModal(true); }}>
              <Plus size={15} /> Add Chapter
            </button>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <span className="card-title">
            <Music size={15} style={{ color: 'var(--orange-400)' }} />
            {chapters.length} chapter{chapters.length !== 1 ? 's' : ''}
          </span>
          <div style={{ display: 'flex', gap: 16, fontSize: 12, color: 'var(--grey-400)' }}>
            <span>👁 Preview = free access</span>
            <span>🔄 Active = enabled</span>
            <span>≡ Drag to reorder</span>
          </div>
        </div>
        <div className="table-wrap">
          {loading ? <div className="loader-wrap"><div className="loader" /></div> : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={chapters.map(c => c.id)} strategy={verticalListSortingStrategy}>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th style={{ width: 36 }}></th>
                      <th style={{ width: 60 }}>Thumb</th>
                      <th>Chapter Name</th>
                      <th>Key</th>
                      <th>Files</th>
                      <th>Preview</th>
                      <th>Active</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {chapters.map(ch => (
                      <SortableChapterRow
                        key={ch.id} ch={ch}
                        tutorialKey={tutorial?.key_value}
                        onEdit={c => { setEditing(c); setModal(true); }}
                        onDelete={id => setConfirm(id)}
                        onToggle={handleToggle}
                        onQR={c => setQrChapterId(c.id)}
                      />
                    ))}
                    {!chapters.length && (
                      <tr><td colSpan={8}>
                        <div className="empty-state"><Music size={36} /><h3>No chapters yet</h3><p>Add chapters to this tutorial volume.</p></div>
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </SortableContext>
            </DndContext>
          )}
        </div>
      </div>

      {modal && (
        <ChapterFormModal
          tutorialId={Number(tutorialId)}
          editing={editing}
          onClose={() => setModal(false)}
          onSaved={() => { setModal(false); load(); }}
        />
      )}

      {qrChapterId && (
        <QRModal chapterId={qrChapterId} onClose={() => setQrChapterId(null)} />
      )}

      <ConfirmDialog open={!!confirm} title="Delete Chapter" message="This chapter will be permanently removed."
        onConfirm={() => doDelete(confirm)} onCancel={() => setConfirm(null)} />
    </div>
  );
}
